import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ChevronLeft, Save, Download, Award, Loader, Upload, Trash2,
  Search, Filter, GraduationCap, ExternalLink, BookOpen, Layers, Calendar, X,
  ZoomIn, ZoomOut, Maximize2, Palette, Type, Image as ImageIcon, Sliders, Eye, RefreshCw,
} from "lucide-react";
import { BASE_URL } from "../../../configs/WebServiceUrl";
import {
  lmsGetCertTemplate, lmsSaveCertTemplate,
  lmsGetCertificates, lmsUploadCertImage,
} from "../api/lmsApi";
import "../lms.css";

export const getFullImageUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (trimmed.startsWith("data:") || trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("blob:")) {
    return trimmed;
  }
  return `${BASE_URL}${trimmed.startsWith("/") ? "" : "/"}${trimmed}`;
};

// ─── 7 Built-in Templates ────────────────────────────────────────────────────
export const CERT_TEMPLATES = [
  { id: "t1", name: "Achievement",    orientation: "portrait",  thumb: "/cert-templates/t1.png" },
  { id: "t2", name: "Frame Classic",  orientation: "portrait",  thumb: "/cert-templates/t2.png" },
  { id: "t3", name: "Modern Border",  orientation: "portrait",  thumb: "/cert-templates/t3.png" },
  { id: "t4", name: "Wide Classic",   orientation: "landscape", thumb: "/cert-templates/t4.png" },
  { id: "t5", name: "Graduation",     orientation: "portrait",  thumb: "/cert-templates/t5.png" },
  { id: "t6", name: "Wide Elegant",   orientation: "landscape", thumb: "/cert-templates/t6.png" },
  { id: "t7", name: "Red & Gold",     orientation: "portrait",  thumb: "/cert-templates/t7.png" },
];

// Clean hue-rotate approach — keeps colors vibrant, no sepia washout
// Templates are navy-blue (~215°) + gold (~45°). hue-rotate shifts both proportionally.
export const COLOR_FILTERS = [
  { id: "none",   label: "Original", color: null,      filter: "none" },
  { id: "teal",   label: "Teal",     color: "#0d9488", filter: "hue-rotate(155deg) saturate(1.2)" },
  { id: "green",  label: "Green",    color: "#16a34a", filter: "hue-rotate(110deg) saturate(1.3)" },
  { id: "purple", label: "Purple",   color: "#7c3aed", filter: "hue-rotate(205deg) saturate(1.2)" },
  { id: "red",    label: "Red",      color: "#dc2626", filter: "hue-rotate(315deg) saturate(1.4)" },
  { id: "maroon", label: "Maroon",   color: "#9f1239", filter: "hue-rotate(295deg) saturate(1.4) brightness(0.82)" },
  { id: "orange", label: "Orange",   color: "#ea580c", filter: "hue-rotate(340deg) saturate(1.3) brightness(1.05)" },
  { id: "indigo", label: "Indigo",   color: "#4338ca", filter: "hue-rotate(230deg) saturate(1.3)" },
  { id: "rose",   label: "Rose",     color: "#e11d48", filter: "hue-rotate(305deg) saturate(1.3) brightness(1.05)" },
  { id: "bw",     label: "B & W",    color: "#374151", filter: "grayscale(1)" },
];

// ─── Logo drop zone on certificate ────────────────────────────────────────────
function LogoDropZone({ label, onDrop }) {
  const [over, setOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const ref = useRef();

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setUploading(true);
    try {
      const res = await lmsUploadCertImage(file);
      if (res.data?.url) onDrop(res.data.url);
      else {
        const reader = new FileReader();
        reader.onload = (ev) => onDrop(ev.target.result);
        reader.readAsDataURL(file);
      }
    } catch {
      const reader = new FileReader();
      reader.onload = (ev) => onDrop(ev.target.result);
      reader.readAsDataURL(file);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      onClick={() => ref.current.click()}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); handleFile(e.dataTransfer.files[0]); }}
      style={{
        width: 120, height: 60, border: `2px dashed ${over ? "#f59e0b" : "#4f46e5"}`,
        borderRadius: 8, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", cursor: "pointer",
        background: over ? "rgba(245,158,11,0.2)" : "rgba(255,255,255,0.9)",
        boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
        transition: "all 0.15s", backdropFilter: "blur(4px)",
      }}
    >
      <Upload size={16} color={over ? "#f59e0b" : "#4f46e5"} />
      <div style={{ fontSize: 10, fontWeight: 700, color: over ? "#f59e0b" : "#4f46e5", marginTop: 3, textAlign: "center" }}>
        {uploading ? "Uploading..." : (over ? "Drop!" : `+ ${label}`)}
      </div>
      <input ref={ref} type="file" accept="image/*" style={{ display: "none" }}
        onChange={(e) => { handleFile(e.target.files[0]); e.target.value = ""; }} />
    </div>
  );
}

// ─── Certificate Preview ──────────────────────────────────────────────────────
export function CertificatePreview({ tmpl, student, printRef, onDropLeft, onDropRight, onLogoMove }) {
  const borderColor  = tmpl.border_color   || "#1a56db";
  const accentColor  = tmpl.accent_color   || "#f59e0b";
  const bgColor      = tmpl.background_color || "#ffffff";
  const watermarkTxt = tmpl.watermark_text || "";
  const orientation  = tmpl.orientation   || "portrait";
  const tplId        = tmpl.template_id   || "t1";
  const tplImg       = CERT_TEMPLATES.find((t) => t.id === tplId)?.thumb || "/cert-templates/t1.png";
  const cfFilter     = COLOR_FILTERS.find((c) => c.id === (tmpl.color_filter || "none"))?.filter || "none";
  const logoPos      = tmpl.logo_position || "both";
  const showLeft     = logoPos === "both" || logoPos === "left";
  const showRight    = logoPos === "both" || logoPos === "right";

  const containerRef = useRef();
  const [drag, setDrag] = useState(null);

  const getScale = () => {
    if (!containerRef.current) return 1;
    return containerRef.current.getBoundingClientRect().width / W;
  };

  const startDrag = (e, side) => {
    if (!onLogoMove) return;
    e.preventDefault();
    e.stopPropagation();
    const x = side === "left" ? (tmpl.logo_left_x ?? 70)  : (tmpl.logo_right_x ?? W - 230);
    const y = side === "left" ? (tmpl.logo_left_y ?? 140) : (tmpl.logo_right_y ?? 140);
    setDrag({ side, startMouseX: e.clientX, startMouseY: e.clientY, startX: x, startY: y });
  };

  const onMouseMove = (e) => {
    if (!drag) return;
    const scale = getScale();
    const dx = (e.clientX - drag.startMouseX) / scale;
    const dy = (e.clientY - drag.startMouseY) / scale;
    onLogoMove(drag.side, Math.round(drag.startX + dx), Math.round(drag.startY + dy));
  };

  const stopDrag = () => setDrag(null);

  // A4 dimensions: portrait 794×1123, landscape 1123×794
  const bodyText     = tmpl.body_text     || "This is proudly presented to";
  const studentName  = student?.name      || "Student Name";
  const rawParent    = student?.parent_name || tmpl.parent_name || "";
  const parentName   = rawParent && !/^\d+$/.test(String(rawParent).trim()) ? String(rawParent).trim() : "";
  const examName     = student?.exam_name || tmpl.exam_name || "Exam / Course Name";
  const centerName   = student?.center_name || tmpl.center_name || tmpl.school_name || "Center / School Name";
  const marks        = student?.marks_obtained != null ? `${student.marks_obtained}` : (tmpl.marks_obtained != null ? `${tmpl.marks_obtained}` : null);
  const grade        = student?.grade || tmpl.grade || null;
  const issueDate    = student?.issued_at
    ? new Date(student.issued_at).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });

  const W = orientation === "landscape" ? 1123 : 794;
  const H = orientation === "landscape" ? 794  : 1123;

  // merge printRef + containerRef
  const setRefs = (el) => {
    containerRef.current = el;
    if (printRef) { if (typeof printRef === "function") printRef(el); else printRef.current = el; }
  };

  return (
    <div
      ref={setRefs}
      onMouseMove={onMouseMove}
      onMouseUp={stopDrag}
      onMouseLeave={stopDrag}
      style={{
        width: W, height: H, background: bgColor,
        fontFamily: "Georgia, 'Times New Roman', serif",
        position: "relative", boxSizing: "border-box",
        boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
        overflow: "hidden", flexShrink: 0,
        cursor: drag ? "grabbing" : "default",
        userSelect: "none",
      }}
    >
      {/* Built-in template background (with color filter) */}
      <img
        src={tplImg} alt=""
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "fill", zIndex: 0, filter: cfFilter }}
      />

      {/* Custom background image (layered on top) */}
      {tmpl.background_url && (
        <img src={tmpl.background_url} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.12, zIndex: 1 }} />
      )}

      {/* Watermark text */}
      {watermarkTxt && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 2,
          display: "flex", alignItems: "center", justifyContent: "center",
          pointerEvents: "none",
        }}>
          <div style={{
            fontSize: orientation === "landscape" ? 90 : 110,
            fontWeight: 900, color: borderColor,
            opacity: 0.07, transform: "rotate(-35deg)", whiteSpace: "nowrap",
            letterSpacing: 8, fontFamily: "Georgia, serif", userSelect: "none",
            textTransform: "uppercase",
          }}>
            {watermarkTxt}
          </div>
        </div>
      )}

      {/* ── Logos — absolutely positioned, draggable in builder mode ── */}
      {showLeft && (
        tmpl.logo_left_url ? (
          <img
            src={getFullImageUrl(tmpl.logo_left_url)} alt="logo"
            draggable={false}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            onMouseDown={(e) => startDrag(e, "left")}
            style={{
              position: "absolute", zIndex: 5,
              left: tmpl.logo_left_x ?? 70,
              top:  tmpl.logo_left_y ?? 140,
              maxHeight: 60, maxWidth: 160, objectFit: "contain",
              cursor: onLogoMove ? (drag?.side === "left" ? "grabbing" : "grab") : "default",
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.15))",
            }}
          />
        ) : onDropLeft ? (
          <div style={{ position: "absolute", zIndex: 5, left: tmpl.logo_left_x ?? 70, top: tmpl.logo_left_y ?? 140 }}>
            <LogoDropZone label="Left Logo" onDrop={onDropLeft} />
          </div>
        ) : null
      )}

      {showRight && (
        tmpl.logo_right_url ? (
          <img
            src={getFullImageUrl(tmpl.logo_right_url)} alt="logo"
            draggable={false}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            onMouseDown={(e) => startDrag(e, "right")}
            style={{
              position: "absolute", zIndex: 5,
              left: tmpl.logo_right_x ?? W - 230,
              top:  tmpl.logo_right_y ?? 140,
              maxHeight: 60, maxWidth: 160, objectFit: "contain",
              cursor: onLogoMove ? (drag?.side === "right" ? "grabbing" : "grab") : "default",
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.15))",
            }}
          />
        ) : onDropRight ? (
          <div style={{ position: "absolute", zIndex: 5, left: tmpl.logo_right_x ?? W - 230, top: tmpl.logo_right_y ?? 140 }}>
            <LogoDropZone label="Right Logo" onDrop={onDropRight} />
          </div>
        ) : null
      )}

      {/* Main content */}
      <div style={{ position: "relative", zIndex: 3, padding: orientation === "landscape" ? "110px 80px 36px" : "200px 70px 48px" }}>

        {/* ── CERTIFICATE title ── */}
        <div style={{ textAlign: "center", marginBottom: 4 }}>
          <span style={{
            fontSize: orientation === "landscape" ? 48 : 52, fontWeight: 900, letterSpacing: 10,
            textTransform: "uppercase", color: borderColor, fontFamily: "Georgia, serif",
            textShadow: `2px 2px 0 ${accentColor}44`,
          }}>
            {tmpl.title || "CERTIFICATE"}
          </span>
        </div>
        <div style={{ textAlign: "center", marginBottom: 22, fontSize: 17, color: accentColor, letterSpacing: 4, fontStyle: "italic", fontWeight: 600 }}>
          — {tmpl.subtitle || "of Achievement"} —
        </div>

        {/* ── Body text ── */}
        <div style={{ textAlign: "center", fontSize: 16, color: "#374151", fontStyle: "italic", marginBottom: 10 }}>
          {bodyText}
        </div>

        {/* ── Student name ── */}
        <div style={{ textAlign: "center", margin: "4px 60px 12px" }}>
          <div style={{ fontSize: 32, fontWeight: 800, color: borderColor, letterSpacing: 2, fontFamily: "Georgia, serif", padding: "4px 0" }}>
            {studentName}
          </div>
        </div>

        {/* ── Parent name (rendered only if valid name exists) ── */}
        {parentName ? (
          <>
            <div style={{ textAlign: "center", fontSize: 15, color: "#6b7280", fontStyle: "italic", marginBottom: 3 }}>
              S/o, D/o, W/o
            </div>
            <div style={{ textAlign: "center", margin: "0 80px 12px" }}>
              <div style={{ fontSize: 20, color: "#374151", fontStyle: "italic", fontWeight: 600, padding: "2px 0" }}>
                {parentName}
              </div>
            </div>
          </>
        ) : null}

        {/* ── Completion text ── */}
        <div style={{ textAlign: "center", fontSize: 15, color: "#374151", fontStyle: "italic", marginBottom: 6 }}>
          for successfully completing the
        </div>

        {/* ── Exam / Course name ── */}
        <div style={{ textAlign: "center", margin: "2px 60px 8px" }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: accentColor, letterSpacing: 1, padding: "2px 0" }}>
            {examName}
          </div>
        </div>

        <div style={{ textAlign: "center", fontSize: 15, color: "#374151", fontStyle: "italic", marginBottom: 6 }}>
          program conducted at
        </div>

        {/* ── Center name ── */}
        <div style={{ textAlign: "center", margin: "2px 80px 18px" }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: "#1f2937", padding: "2px 0" }}>{centerName}</div>
        </div>

        {/* ── Marks / Grade row ── */}
        {(tmpl.show_marks || tmpl.show_grade) && (marks || grade) && (
          <div style={{ display: "flex", justifyContent: "center", gap: 24, marginBottom: 18 }}>
            {tmpl.show_marks && marks && (
              <div style={{ textAlign: "center", padding: "8px 22px", background: `${borderColor}11`, border: `2px solid ${borderColor}`, borderRadius: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", letterSpacing: 1 }}>MARKS OBTAINED</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: borderColor }}>
                  {marks} {student?.total_marks || tmpl.total_marks ? `/ ${student?.total_marks || tmpl.total_marks}` : ""}
                </div>
              </div>
            )}
            {tmpl.show_grade && grade && (
              <div style={{ textAlign: "center", padding: "8px 22px", background: `${accentColor}22`, border: `2px solid ${accentColor}`, borderRadius: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", letterSpacing: 1 }}>GRADE</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: accentColor }}>{grade}</div>
              </div>
            )}
          </div>
        )}

        {/* ── Divider ── */}
        <div style={{ height: 2, background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`, margin: "0 0 18px" }} />

        {/* ── Footer: Signatures + optional Stamp ── */}
        <div style={{ display: "flex", justifyContent: tmpl.show_stamp ? "space-between" : "space-around", alignItems: "flex-end" }}>
          {/* Signature 1 */}
          <div style={{ textAlign: "center", minWidth: 140 }}>
            {tmpl.signature1_url
              ? <img src={getFullImageUrl(tmpl.signature1_url)} alt="Signature 1" onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ height: 50, objectFit: "contain", marginBottom: 4 }} />
              : <div style={{ height: 36, marginBottom: 4 }} />
            }
            <div style={{ borderTop: `1.5px solid #374151`, paddingTop: 5, fontSize: 11, fontWeight: 700, color: "#374151" }}>
              {tmpl.signature1_label || "Authorized Signatory"}
            </div>
          </div>

          {/* Stamp — only if show_stamp enabled */}
          {tmpl.show_stamp && (
            <div style={{ textAlign: "center" }}>
              {tmpl.stamp_url
                ? <img src={getFullImageUrl(tmpl.stamp_url)} alt="Stamp" onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ height: 80, width: 80, objectFit: "contain" }} />
                : <div style={{ height: 80, width: 80, borderRadius: "50%", border: `3px dashed ${borderColor}`, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#d1d5db", fontSize: 10, margin: "0 auto" }}>STAMP</div>
              }
            </div>
          )}

          {/* Signature 2 */}
          <div style={{ textAlign: "center", minWidth: 140 }}>
            {tmpl.signature2_url
              ? <img src={getFullImageUrl(tmpl.signature2_url)} alt="Signature 2" onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ height: 50, objectFit: "contain", marginBottom: 4 }} />
              : <div style={{ height: 36, marginBottom: 4 }} />
            }
            <div style={{ borderTop: `1.5px solid #374151`, paddingTop: 5, fontSize: 11, fontWeight: 700, color: "#374151" }}>
              {tmpl.signature2_label || "Principal / Director"}
            </div>
          </div>
        </div>

        {/* ── Date ── */}
        {tmpl.show_date && (
          <div style={{ textAlign: "center", marginTop: 14, fontSize: 12, color: "#9ca3af", letterSpacing: 1 }}>
            Date: {issueDate}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Upload helper with Drag & Drop ───────────────────────────────────────────
function ImageUploadField({ label, value, onChange, onClear }) {
  const ref = useRef();
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setUploading(true);
    // Instant local preview
    const previewUrl = URL.createObjectURL(file);
    onChange(previewUrl);
    try {
      const res = await lmsUploadCertImage(file);
      if (res.data?.url) {
        onChange(res.data.url);
      }
    } catch {
      const reader = new FileReader();
      reader.onload = (ev) => onChange(ev.target.result);
      reader.readAsDataURL(file);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const displayUrl = getFullImageUrl(value);

  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", display: "block", marginBottom: 4, letterSpacing: 0.5 }}>
        {label} {uploading && <span style={{ color: "#4f46e5", fontSize: 10 }}>(Uploading...)</span>}
      </label>
      {value ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#f8fafc" }}>
          <img
            src={displayUrl}
            alt={label}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            style={{ height: 40, maxWidth: 120, objectFit: "contain", borderRadius: 4 }}
          />
          <div style={{ flex: 1, fontSize: 10, color: "#9ca3af" }}>{uploading ? "Uploading..." : "Uploaded"}</div>
          <button onClick={onClear} style={{ background: "#fee2e2", border: "none", color: "#dc2626", borderRadius: 6, padding: "4px 8px", cursor: "pointer", display: "flex", alignItems: "center", gap: 3, fontSize: 11, flexShrink: 0 }}>
            <Trash2 size={11} /> Remove
          </button>
        </div>
      ) : (
        <div
          onClick={() => ref.current.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            gap: 6, padding: "14px 10px", cursor: "pointer",
            border: `2px dashed ${dragging ? "#f59e0b" : "#d1d5db"}`,
            borderRadius: 10,
            background: dragging ? "#fffbeb" : "#f8fafc",
            transition: "all 0.15s",
          }}
        >
          <Upload size={18} color={dragging ? "#f59e0b" : "#9ca3af"} />
          <div style={{ fontSize: 11, fontWeight: 700, color: dragging ? "#d97706" : "#6b7280" }}>
            {uploading ? "Uploading..." : (dragging ? "Drop to upload" : "Click or drag & drop")}
          </div>
          <div style={{ fontSize: 10, color: "#9ca3af" }}>PNG, JPG, SVG up to 10MB</div>
        </div>
      )}
      <input ref={ref} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => {
        handleFile(e.target.files[0]);
        e.target.value = "";
      }} />
    </div>
  );
}

// ─── Main Builder Page ─────────────────────────────────────────────────────────
export default function CertificateBuilder() {
  const nav = useNavigate();
  const schoolId = (() => { try { return JSON.parse(localStorage.getItem("userData") || "{}").admin_id || null; } catch { return null; } })();

  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [certs,   setCerts]   = useState([]);
  const [tab,     setTab]     = useState("builder"); // builder | issued
  const printRef = useRef();

  const [tmpl, setTmpl] = useState({
    title: "CERTIFICATE", subtitle: "of Achievement",
    body_text: "This is proudly presented to",
    logo_left_url: "", logo_right_url: "",
    logo_left_x: 70, logo_left_y: 140, logo_right_x: 564, logo_right_y: 140,
    signature1_url: "", signature1_label: "Authorized Signatory",
    signature2_url: "", signature2_label: "Principal / Director",
    stamp_url: "", background_url: "",
    background_color: "#ffffff", watermark_text: "",
    border_color: "#1a56db", accent_color: "#f59e0b",
    show_marks: true, show_grade: true, show_date: true, show_stamp: false,
    template_id: "t1", orientation: "portrait", color_filter: "none", logo_position: "both",
  });

  useEffect(() => {
    const init = async () => {
      try {
        const [tmplRes, certsRes] = await Promise.all([
          lmsGetCertTemplate({ school_id: schoolId }).catch(() => ({ data: null })),
          lmsGetCertificates({ school_id: schoolId }).catch(() => ({ data: { data: [] } })),
        ]);
        if (tmplRes?.data) setTmpl((p) => ({ ...p, ...tmplRes.data }));
        setCerts(certsRes?.data?.data || []);
      } catch {
        toast.error("Failed to load certificate data");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [schoolId]);

  const isTeacher = window.location.pathname.startsWith("/teacher-lms");
  const basePath = isTeacher ? "/teacher-lms" : "/lms";

  const [selectedExamFilter, setSelectedExamFilter] = useState("all");
  const [selectedModeFilter, setSelectedModeFilter] = useState("all"); // all | online | offline
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSection, setActiveSection] = useState("design"); // design | content | media | badges
  const [zoomScale, setZoomScale] = useState(null); // null => auto fit

  const getGradeStyle = (grade) => {
    if (!grade) return { bg: "#f3f4f6", color: "#4b5563" };
    const g = String(grade).toLowerCase();
    if (g.includes("out") || g.includes("excel") || g.includes("a+")) {
      return { bg: "#dcfce7", color: "#15803d" };
    }
    if (g.includes("very") || g.includes("good") || g.includes("a") || g.includes("b+")) {
      return { bg: "#dbeafe", color: "#1d4ed8" };
    }
    if (g.includes("sat") || g.includes("b") || g.includes("c")) {
      return { bg: "#fef3c7", color: "#b45309" };
    }
    return { bg: "#ffe4e6", color: "#be123c" };
  };

  // Group certificates by exam
  const examGroups = useMemo(() => {
    const map = new Map();
    certs.forEach((c) => {
      const examKey = c.exam_id != null ? String(c.exam_id) : `manual_${c.id}`;
      if (!map.has(examKey)) {
        map.set(examKey, {
          exam_id: c.exam_id,
          exam_name: c.exam_name || "General Assessment",
          exam_type: c.exam_type || (c.exam_mode === "offline" ? "offline" : "online"),
          class_name: c.class_name,
          subject_name: c.subject_name,
          total_marks: c.total_marks,
          items: [],
        });
      }
      map.get(examKey).items.push(c);
    });
    return Array.from(map.values());
  }, [certs]);

  // Unique exams for dropdown filter
  const examOptions = useMemo(() => {
    return examGroups.map((g) => ({
      id: g.exam_id ? String(g.exam_id) : "manual",
      name: g.exam_name,
      count: g.items.length,
      mode: g.exam_type,
    }));
  }, [examGroups]);

  // Filtered groups according to search, exam dropdown, and mode
  const filteredExamGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return examGroups
      .filter((g) => {
        if (selectedExamFilter !== "all" && String(g.exam_id) !== String(selectedExamFilter)) {
          return false;
        }
        if (selectedModeFilter !== "all") {
          const isOffline = g.exam_type === "offline";
          if (selectedModeFilter === "offline" && !isOffline) return false;
          if (selectedModeFilter === "online" && isOffline) return false;
        }
        return true;
      })
      .map((g) => {
        if (!q) return g;
        const matchingItems = g.items.filter((c) => {
          const sName = (c.student_name || "").toLowerCase();
          const sCode = (c.student_code || "").toLowerCase();
          const eName = (g.exam_name || "").toLowerCase();
          const clsName = (g.class_name || "").toLowerCase();
          const subName = (g.subject_name || "").toLowerCase();
          return (
            sName.includes(q) ||
            sCode.includes(q) ||
            eName.includes(q) ||
            clsName.includes(q) ||
            subName.includes(q)
          );
        });
        return { ...g, items: matchingItems };
      })
      .filter((g) => g.items.length > 0);
  }, [examGroups, selectedExamFilter, selectedModeFilter, searchQuery]);

  const totalFilteredCerts = useMemo(() => {
    return filteredExamGroups.reduce((sum, g) => sum + g.items.length, 0);
  }, [filteredExamGroups]);

  const set = (key, val) => setTmpl((p) => ({ ...p, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await lmsSaveCertTemplate({ ...tmpl, school_id: schoolId });
      toast.success("School certificate template saved!");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save");
    } finally { setSaving(false); }
  };

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const isLandscape = tmpl.orientation === "landscape";
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>Certificate</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body {
          width: ${isLandscape ? "297mm" : "210mm"};
          height: ${isLandscape ? "210mm" : "297mm"};
          background: #fff; overflow: hidden;
        }
        @page { size: A4 ${isLandscape ? "landscape" : "portrait"}; margin: 0; }
        @media print {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          color-adjust: exact;
        }
        .cert-wrap {
          width: ${isLandscape ? "297mm" : "210mm"};
          height: ${isLandscape ? "210mm" : "297mm"};
          display: flex; align-items: center; justify-content: center;
          page-break-after: avoid; page-break-inside: avoid; overflow: hidden;
        }
        .cert-wrap > * {
          transform-origin: center center;
          transform: scale(0.97);
        }
      </style></head>
      <body><div class="cert-wrap">${content.outerHTML}</div></body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 600);
  };

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
      <Loader size={32} className="ms2-spin" color="#4f46e5" />
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: "#f1f5f9" }}>

      {/* Top bar */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "10px 20px", display: "flex", alignItems: "center", gap: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <button onClick={() => nav(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", display: "flex", alignItems: "center", gap: 5, fontSize: 13, fontWeight: 600 }}>
          <ChevronLeft size={16} /> Back
        </button>
        <div style={{ width: 1, height: 20, background: "#e5e7eb" }} />
        <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#f59e0b,#d97706)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Award size={18} color="#fff" />
        </div>
        <div>
          <div style={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>
            School Certificate Theme
          </div>
          <div style={{ fontSize: 11, color: "#6b7280" }}>
            Default School Template · Customize Certificate Theme & Branding
          </div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button onClick={handleSave} disabled={saving} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "7px 16px",
            borderRadius: 8, border: "none", background: saving ? "#d1d5db" : "linear-gradient(135deg,#16a34a,#15803d)",
            color: "#fff", fontSize: 12, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer",
          }}>
            {saving ? <Loader size={13} className="ms2-spin" /> : <Save size={13} />}
            {saving ? "Saving..." : "Save Template"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: "1px solid #e5e7eb", background: "#fff", paddingLeft: 20 }}>
        {[
          { key: "builder", label: "Certificate Builder" },
          { key: "issued",  label: `Issued Certificates (${certs.length})` },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: "10px 20px", border: "none", background: "none", cursor: "pointer",
            borderBottom: `3px solid ${tab === t.key ? "#f59e0b" : "transparent"}`,
            color: tab === t.key ? "#d97706" : "#6b7280",
            fontWeight: tab === t.key ? 700 : 500, fontSize: 13,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "builder" && (
        <div style={{ display: "flex", alignItems: "stretch", height: "calc(100vh - 112px)", overflow: "hidden", background: "#f8fafc" }}>

          {/* LEFT — Studio Controls Sidebar */}
          <div style={{
            width: 380,
            background: "#ffffff",
            borderRight: "1px solid #e2e8f0",
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            height: "100%",
            boxShadow: "2px 0 8px rgba(0,0,0,0.02)"
          }}>
            {/* Category Navigation Tabs */}
            <div style={{
              padding: "10px 12px",
              borderBottom: "1px solid #e2e8f0",
              background: "#fafafa",
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 6
            }}>
              {[
                { id: "design",  label: "Design",   icon: Palette },
                { id: "content", label: "Content",  icon: Type },
                { id: "media",   label: "Branding", icon: ImageIcon },
                { id: "badges",  label: "Options",  icon: Sliders },
              ].map((sec) => {
                const Icon = sec.icon;
                const active = activeSection === sec.id;
                return (
                  <button
                    key={sec.id}
                    onClick={() => setActiveSection(sec.id)}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 4,
                      padding: "7px 4px",
                      borderRadius: 8,
                      border: active ? "1.5px solid #4f46e5" : "1.5px solid transparent",
                      background: active ? "#eef2ff" : "transparent",
                      color: active ? "#4338ca" : "#64748b",
                      fontWeight: active ? 700 : 600,
                      fontSize: 11,
                      cursor: "pointer",
                      transition: "all 0.15s"
                    }}
                  >
                    <Icon size={16} color={active ? "#4f46e5" : "#64748b"} />
                    <span>{sec.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Scrollable Settings Panel Body */}
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 18px" }}>

              {/* 1. DESIGN & THEME */}
              {activeSection === "design" && (
                <div>
                  {/* Template Selector */}
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "#334155", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Template Style
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
                      {CERT_TEMPLATES.map((t) => {
                        const isSelected = tmpl.template_id === t.id;
                        return (
                          <div
                            key={t.id}
                            onClick={() => { set("template_id", t.id); set("orientation", t.orientation); }}
                            style={{
                              cursor: "pointer", borderRadius: 8, overflow: "hidden",
                              border: isSelected ? "2.5px solid #4f46e5" : "1.5px solid #e2e8f0",
                              boxShadow: isSelected ? "0 0 0 2px #c7d2fe" : "none",
                              position: "relative",
                              transition: "all 0.15s"
                            }}
                          >
                            <img src={t.thumb} alt={t.name} style={{ width: "100%", aspectRatio: t.orientation === "landscape" ? "4/3" : "3/4", objectFit: "cover", display: "block" }} />
                            <div style={{ fontSize: 8, fontWeight: 700, textAlign: "center", padding: "3px 2px", background: isSelected ? "#4f46e5" : "#f8fafc", color: isSelected ? "#fff" : "#64748b" }}>
                              {t.name}
                            </div>
                            {t.orientation === "landscape" && (
                              <div style={{ position: "absolute", top: 2, right: 2, fontSize: 7, fontWeight: 800, background: "#1a56db", color: "#fff", borderRadius: 3, padding: "1px 3px" }}>L</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Orientation */}
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "#334155", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Canvas Orientation
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      {[
                        { id: "landscape", label: "Landscape (A4)" },
                        { id: "portrait",  label: "Portrait (A4)" },
                      ].map((o) => (
                        <button
                          key={o.id}
                          onClick={() => set("orientation", o.id)}
                          style={{
                            flex: 1, padding: "7px 0", borderRadius: 8, cursor: "pointer", fontSize: 11, fontWeight: 700,
                            border: `2px solid ${tmpl.orientation === o.id ? "#4f46e5" : "#e2e8f0"}`,
                            background: tmpl.orientation === o.id ? "#eef2ff" : "#f8fafc",
                            color: tmpl.orientation === o.id ? "#4338ca" : "#64748b",
                            transition: "all 0.15s"
                          }}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Color Themes */}
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "#334155", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Template Color Theme
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6 }}>
                      {COLOR_FILTERS.map((f) => {
                        const isSelected = (tmpl.color_filter || "none") === f.id;
                        return (
                          <div
                            key={f.id}
                            onClick={() => set("color_filter", f.id)}
                            style={{
                              borderRadius: 8, padding: "6px 2px", textAlign: "center", cursor: "pointer",
                              border: isSelected ? "2px solid #4f46e5" : "1.5px solid #e2e8f0",
                              background: isSelected ? "#eef2ff" : "#f8fafc",
                              transition: "all 0.15s"
                            }}
                          >
                            <div style={{
                              width: 16, height: 16, borderRadius: "50%", margin: "0 auto 3px",
                              background: f.color || "linear-gradient(135deg,#1a56db,#f59e0b)",
                              border: "1px solid rgba(0,0,0,0.15)",
                            }} />
                            <div style={{ fontSize: 9, fontWeight: 600, color: isSelected ? "#4338ca" : "#64748b" }}>{f.label}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Text Accent Colors */}
                  <div style={{ marginBottom: 18, padding: 12, background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "#334155", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Text Accent Colors
                    </div>
                    <div style={{ display: "flex", gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 10, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 4 }}>Primary / Title</label>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <input type="color" value={tmpl.border_color || "#1a56db"} onChange={(e) => set("border_color", e.target.value)} style={{ width: 34, height: 30, border: "none", padding: 2, cursor: "pointer", borderRadius: 6 }} />
                          <span style={{ fontSize: 11, color: "#334155", fontWeight: 600 }}>{tmpl.border_color || "#1a56db"}</span>
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 10, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 4 }}>Secondary / Gold</label>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <input type="color" value={tmpl.accent_color || "#f59e0b"} onChange={(e) => set("accent_color", e.target.value)} style={{ width: 34, height: 30, border: "none", padding: 2, cursor: "pointer", borderRadius: 6 }} />
                          <span style={{ fontSize: 11, color: "#334155", fontWeight: 600 }}>{tmpl.accent_color || "#f59e0b"}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Background Color */}
                  <div style={{ marginBottom: 12, padding: 12, background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                      <label style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>Background Tint</label>
                      <button onClick={() => set("background_color", "#ffffff")} style={{ fontSize: 10, padding: "2px 6px", borderRadius: 5, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", color: "#64748b" }}>Reset</button>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                      <input type="color" value={tmpl.background_color || "#ffffff"} onChange={(e) => set("background_color", e.target.value)} style={{ width: 28, height: 26, border: "none", padding: 1, cursor: "pointer", borderRadius: 6 }} />
                      {["#ffffff","#fef9f0","#f0f7ff","#f0fff4","#fff0f6","#fafaf0","#f5f0ff"].map((c) => (
                        <div key={c} onClick={() => set("background_color", c)} style={{
                          width: 22, height: 22, borderRadius: 6, background: c, cursor: "pointer",
                          border: tmpl.background_color === c ? "2.5px solid #4f46e5" : "1.5px solid #cbd5e1",
                        }} />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* 2. TEXT & CONTENT */}
              {activeSection === "content" && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "#334155", marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Certificate Headings & Body
                  </div>
                  {[
                    { key: "title",     label: "Certificate Title",  placeholder: "CERTIFICATE" },
                    { key: "subtitle",  label: "Subtitle / Tagline", placeholder: "of Achievement" },
                    { key: "body_text", label: "Opening Intro Text", placeholder: "This is proudly presented to" },
                  ].map(({ key, label, placeholder }) => (
                    <div key={key} style={{ marginBottom: 14 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 4 }}>{label}</label>
                      <input
                        value={tmpl[key] || ""}
                        onChange={(e) => set(key, e.target.value)}
                        placeholder={placeholder}
                        style={{ width: "100%", padding: "8px 10px", borderRadius: 7, border: "1.5px solid #e2e8f0", fontSize: 12, boxSizing: "border-box", outline: "none" }}
                      />
                    </div>
                  ))}

                  <div style={{ marginTop: 16, padding: 12, background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 4 }}>Watermark Diagonal Text</label>
                    <input
                      value={tmpl.watermark_text || ""}
                      onChange={(e) => set("watermark_text", e.target.value)}
                      placeholder='e.g. SAMPLE, CERTIFIED, school name...'
                      style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "1.5px solid #e2e8f0", fontSize: 12, boxSizing: "border-box", outline: "none" }}
                    />
                    <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>Displays diagonally in light background opacity across certificate</div>
                  </div>
                </div>
              )}

              {/* 3. LOGOS, SIGNATURES & BRANDING */}
              {activeSection === "media" && (
                <div>
                  {/* Logo Positioning */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "#334155", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Header Logos Visibility
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                      {[
                        { id: "both",  label: "Both (Left + Right)" },
                        { id: "left",  label: "Left Only" },
                        { id: "right", label: "Right Only" },
                        { id: "none",  label: "Hide All Logos" },
                      ].map((lp) => (
                        <button
                          key={lp.id}
                          onClick={() => set("logo_position", lp.id)}
                          style={{
                            padding: "6px 8px", borderRadius: 7, cursor: "pointer", fontSize: 10, fontWeight: 700,
                            border: `2px solid ${tmpl.logo_position === lp.id || (!tmpl.logo_position && lp.id === "both") ? "#4f46e5" : "#e2e8f0"}`,
                            background: tmpl.logo_position === lp.id || (!tmpl.logo_position && lp.id === "both") ? "#eef2ff" : "#f8fafc",
                            color: tmpl.logo_position === lp.id || (!tmpl.logo_position && lp.id === "both") ? "#4338ca" : "#64748b",
                          }}
                        >
                          {lp.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Logos Upload */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "#334155", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Logo Files
                    </div>
                    <ImageUploadField label="Left Logo" value={tmpl.logo_left_url} onChange={(v) => set("logo_left_url", v)} onClear={() => set("logo_left_url", "")} />
                    <ImageUploadField label="Right Logo" value={tmpl.logo_right_url} onChange={(v) => set("logo_right_url", v)} onClear={() => set("logo_right_url", "")} />
                  </div>

                  {/* Signatures */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "#334155", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Signatures & Signatories
                    </div>
                    <ImageUploadField label="Signature 1" value={tmpl.signature1_url} onChange={(v) => set("signature1_url", v)} onClear={() => set("signature1_url", "")} />
                    <input
                      value={tmpl.signature1_label || ""}
                      onChange={(e) => set("signature1_label", e.target.value)}
                      placeholder="Authorized Signatory"
                      style={{ width: "100%", marginBottom: 10, padding: "6px 10px", borderRadius: 7, border: "1.5px solid #e2e8f0", fontSize: 11, boxSizing: "border-box" }}
                    />
                    <ImageUploadField label="Signature 2" value={tmpl.signature2_url} onChange={(v) => set("signature2_url", v)} onClear={() => set("signature2_url", "")} />
                    <input
                      value={tmpl.signature2_label || ""}
                      onChange={(e) => set("signature2_label", e.target.value)}
                      placeholder="Principal / Director"
                      style={{ width: "100%", padding: "6px 10px", borderRadius: 7, border: "1.5px solid #e2e8f0", fontSize: 11, boxSizing: "border-box" }}
                    />
                  </div>

                  {/* Stamp & Custom Background */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "#334155", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Official Stamp & Custom Watermark Image
                    </div>
                    <ImageUploadField label="Official School Stamp" value={tmpl.stamp_url} onChange={(v) => set("stamp_url", v)} onClear={() => set("stamp_url", "")} />
                    <ImageUploadField label="Custom Background Pattern" value={tmpl.background_url} onChange={(v) => set("background_url", v)} onClear={() => set("background_url", "")} />
                  </div>
                </div>
              )}

              {/* 4. VISIBILITY & BADGES */}
              {activeSection === "badges" && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "#334155", marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Show / Hide Elements
                  </div>
                  <div style={{ padding: 14, background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                    {[
                      { key: "show_marks", label: "Show Marks Obtained", desc: "Display student score badge" },
                      { key: "show_grade", label: "Show Grade", desc: "Display performance grade" },
                      { key: "show_date",  label: "Show Issue Date", desc: "Display certified date at footer" },
                      { key: "show_stamp", label: "Show Official Stamp", desc: "Show seal badge on certificate" },
                    ].map(({ key, label, desc }) => (
                      <label key={key} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={!!tmpl[key]}
                          onChange={(e) => set(key, e.target.checked)}
                          style={{ width: 16, height: 16, accentColor: "#4f46e5", cursor: "pointer", marginTop: 2 }}
                        />
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>{label}</div>
                          <div style={{ fontSize: 10, color: "#64748b" }}>{desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — Live Preview Canvas (No Scrollbars, Auto Fit) */}
          {(() => {
            const isLandscape = tmpl.orientation === "landscape";
            const W = isLandscape ? 1123 : 794;
            const H = isLandscape ? 794 : 1123;
            const currentScale = zoomScale || (isLandscape ? 0.60 : 0.48);

            return (
              <div style={{
                flex: 1,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                position: "relative"
              }}>
                {/* Preview Toolbar */}
                <div style={{
                  padding: "8px 18px",
                  background: "#ffffff",
                  borderBottom: "1px solid #e2e8f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                  zIndex: 10
                }}>
                  {/* Left info badge */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>Live Preview</span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 6,
                      background: "#eef2ff", color: "#4338ca", border: "1px solid #c7d2fe"
                    }}>
                      A4 {isLandscape ? "Landscape · 1123×794" : "Portrait · 794×1123"}
                    </span>
                  </div>

                  {/* Center Zoom Controls */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#f1f5f9", padding: "3px 6px", borderRadius: 8 }}>
                    <button
                      title="Zoom Out"
                      onClick={() => setZoomScale(Math.max(0.35, +(currentScale - 0.05).toFixed(2)))}
                      style={{ border: "none", background: "#fff", borderRadius: 5, padding: "4px 8px", cursor: "pointer", display: "flex", alignItems: "center", color: "#475569" }}
                    >
                      <ZoomOut size={13} />
                    </button>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#334155", minWidth: 44, textAlign: "center" }}>
                      {Math.round(currentScale * 100)}%
                    </span>
                    <button
                      title="Zoom In"
                      onClick={() => setZoomScale(Math.min(1.0, +(currentScale + 0.05).toFixed(2)))}
                      style={{ border: "none", background: "#fff", borderRadius: 5, padding: "4px 8px", cursor: "pointer", display: "flex", alignItems: "center", color: "#475569" }}
                    >
                      <ZoomIn size={13} />
                    </button>
                    <div style={{ width: 1, height: 14, background: "#cbd5e1", margin: "0 2px" }} />
                    <button
                      onClick={() => setZoomScale(null)}
                      style={{
                        border: "none",
                        background: zoomScale === null ? "#e0e7ff" : "#fff",
                        color: zoomScale === null ? "#3730a3" : "#475569",
                        borderRadius: 5,
                        padding: "3px 8px",
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: "pointer"
                      }}
                    >
                      Fit Screen
                    </button>
                    <button
                      onClick={() => setZoomScale(1.0)}
                      style={{
                        border: "none",
                        background: zoomScale === 1.0 ? "#e0e7ff" : "#fff",
                        color: zoomScale === 1.0 ? "#3730a3" : "#475569",
                        borderRadius: 5,
                        padding: "3px 8px",
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: "pointer"
                      }}
                    >
                      100%
                    </button>
                  </div>

                  {/* Print button */}
                  <button
                    onClick={handlePrint}
                    style={{
                      display: "flex", alignItems: "center", gap: 5, padding: "6px 14px",
                      borderRadius: 8, border: "1.5px solid #f59e0b", background: "#fffbeb",
                      color: "#d97706", fontSize: 12, fontWeight: 700, cursor: "pointer",
                      transition: "all 0.15s"
                    }}
                  >
                    <Download size={13} /> Print / Download
                  </button>
                </div>

                {/* Viewport Canvas (Auto scrollable when zoomed in / 100%, centered when fitting) */}
                <div style={{
                  flex: 1,
                  display: "flex",
                  overflow: "auto",
                  padding: "24px",
                  background: "#f1f5f9"
                }}>
                  <div style={{
                    width: Math.round(W * currentScale),
                    height: Math.round(H * currentScale),
                    position: "relative",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
                    borderRadius: 8,
                    overflow: "hidden",
                    flexShrink: 0,
                    margin: "auto",
                    transition: "width 0.15s ease, height 0.15s ease",
                    background: "#ffffff"
                  }}>
                    <div style={{
                      width: W,
                      height: H,
                      transform: `scale(${currentScale})`,
                      transformOrigin: "top left"
                    }}>
                      <CertificatePreview
                        tmpl={{ ...tmpl, exam_name: "Sample Examination 2026" }}
                        student={null}
                        printRef={printRef}
                        onDropLeft={(v) => set("logo_left_url", v)}
                        onDropRight={(v) => set("logo_right_url", v)}
                        onLogoMove={(side, x, y) => {
                          if (side === "left")  { set("logo_left_x", x);  set("logo_left_y", y); }
                          else                  { set("logo_right_x", x); set("logo_right_y", y); }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Issued Certificates Tab — Exam-wise */}
      {tab === "issued" && (
        <div style={{ padding: "20px 24px", maxWidth: 1400, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
          
          {/* Top Summary & Stats Banner */}
          <div style={{
            background: "#fff",
            borderRadius: 14,
            border: "1px solid #e2e8f0",
            padding: "16px 20px",
            marginBottom: 20,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 44, height: 44, borderRadius: 12,
                background: "linear-gradient(135deg, #4f46e5, #3730a3)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#fff", boxShadow: "0 4px 10px rgba(79,70,229,0.25)"
              }}>
                <Award size={22} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
                  Exam-Wise Issued Certificates
                </h3>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748b" }}>
                  Filter and inspect all student certificates issued by exam
                </p>
              </div>
            </div>

            {/* Quick Stats Badges */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ padding: "6px 14px", borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>Total Issued:</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>{certs.length}</span>
              </div>
              <div style={{ padding: "6px 14px", borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>Total Exams:</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: "#4f46e5" }}>{examGroups.length}</span>
              </div>
              <div style={{ padding: "6px 14px", borderRadius: 10, background: "#f0fdf4", border: "1px solid #bbf7d0", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#166534" }}>Online Issued:</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: "#16a34a" }}>
                  {certs.filter((c) => c.exam_type !== "offline").length}
                </span>
              </div>
              <div style={{ padding: "6px 14px", borderRadius: 10, background: "#fffbeb", border: "1px solid #fde68a", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#92400e" }}>Offline Issued:</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: "#d97706" }}>
                  {certs.filter((c) => c.exam_type === "offline").length}
                </span>
              </div>
            </div>
          </div>

          {/* Search and Filters Toolbar */}
          <div style={{
            background: "#fff",
            borderRadius: 14,
            border: "1px solid #e2e8f0",
            padding: "14px 18px",
            marginBottom: 22,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 12,
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
          }}>
            {/* Search Input */}
            <div style={{ flex: "1 1 260px", position: "relative" }}>
              <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search student, roll number, or exam..."
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "8px 32px 8px 34px",
                  borderRadius: 8,
                  border: "1.5px solid #e2e8f0",
                  fontSize: 12,
                  outline: "none",
                  color: "#1e293b",
                  background: "#f8fafc",
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  style={{
                    position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 0
                  }}
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Exam Dropdown Selector */}
            <div style={{ minWidth: 220, position: "relative" }}>
              <select
                value={selectedExamFilter}
                onChange={(e) => setSelectedExamFilter(e.target.value)}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1.5px solid #e2e8f0",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#334155",
                  background: "#fff",
                  outline: "none",
                  cursor: "pointer"
                }}
              >
                <option value="all">All Exams ({certs.length} Certificates)</option>
                {examOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name} ({opt.count} {opt.mode === "offline" ? "• Offline" : "• Online"})
                  </option>
                ))}
              </select>
            </div>

            {/* Mode Filter Pills */}
            <div style={{ display: "flex", gap: 6, background: "#f1f5f9", padding: 4, borderRadius: 8 }}>
              {[
                { key: "all", label: "All" },
                { key: "online", label: "Online" },
                { key: "offline", label: "Offline" },
              ].map((m) => (
                <button
                  key={m.key}
                  onClick={() => setSelectedModeFilter(m.key)}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 6,
                    border: "none",
                    background: selectedModeFilter === m.key ? "#fff" : "transparent",
                    color: selectedModeFilter === m.key ? "#4f46e5" : "#64748b",
                    fontSize: 11,
                    fontWeight: selectedModeFilter === m.key ? 700 : 500,
                    boxShadow: selectedModeFilter === m.key ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                    cursor: "pointer",
                    transition: "all 0.15s"
                  }}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {/* Result count */}
            <div style={{ marginLeft: "auto", fontSize: 12, fontWeight: 600, color: "#64748b" }}>
              Showing <strong style={{ color: "#0f172a" }}>{totalFilteredCerts}</strong> certificates in <strong style={{ color: "#0f172a" }}>{filteredExamGroups.length}</strong> exams
            </div>
          </div>

          {/* Exam Groups List */}
          {filteredExamGroups.length === 0 ? (
            <div style={{
              textAlign: "center",
              padding: "60px 20px",
              background: "#fff",
              borderRadius: 14,
              border: "1.5px dashed #cbd5e1"
            }}>
              <Award size={48} style={{ color: "#cbd5e1", marginBottom: 12 }} />
              <h4 style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 700, color: "#334155" }}>
                No issued certificates found
              </h4>
              <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>
                {searchQuery || selectedExamFilter !== "all" || selectedModeFilter !== "all"
                  ? "Try adjusting your search query or exam filter criteria."
                  : "All certificates issued across your school exams will be listed here exam-wise."}
              </p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              {filteredExamGroups.map((group, groupIdx) => {
                const isOffline = group.exam_type === "offline";
                const resultsPath = isOffline
                  ? `${basePath}/exams/${group.exam_id}/offline-results`
                  : `${basePath}/exams/${group.exam_id}/results`;

                return (
                  <div
                    key={group.exam_id || `group_${groupIdx}`}
                    style={{
                      background: "#fff",
                      borderRadius: 14,
                      border: "1.5px solid #e2e8f0",
                      overflow: "hidden",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.03)"
                    }}
                  >
                    {/* Exam Group Header */}
                    <div style={{
                      padding: "14px 20px",
                      background: isOffline
                        ? "linear-gradient(90deg, #fffbeb, #ffffff)"
                        : "linear-gradient(90deg, #eef2ff, #ffffff)",
                      borderBottom: "1px solid #e2e8f0",
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                          width: 34, height: 34, borderRadius: 8,
                          background: isOffline ? "#fef3c7" : "#e0e7ff",
                          color: isOffline ? "#b45309" : "#4338ca",
                          display: "flex", alignItems: "center", justifyContent: "center"
                        }}>
                          <BookOpen size={17} />
                        </div>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>
                              {group.exam_name}
                            </span>
                            <span style={{
                              fontSize: 10,
                              fontWeight: 700,
                              padding: "2px 8px",
                              borderRadius: 20,
                              background: isOffline ? "#fef3c7" : "#e0e7ff",
                              color: isOffline ? "#b45309" : "#3730a3",
                              border: `1px solid ${isOffline ? "#fde68a" : "#c7d2fe"}`
                            }}>
                              {isOffline ? "Offline Exam" : "Online Exam"}
                            </span>
                            {group.class_name && (
                              <span style={{
                                fontSize: 10,
                                fontWeight: 600,
                                padding: "2px 8px",
                                borderRadius: 6,
                                background: "#f1f5f9",
                                color: "#475569",
                                border: "1px solid #e2e8f0"
                              }}>
                                Class: {group.class_name}
                              </span>
                            )}
                            {group.subject_name && (
                              <span style={{
                                fontSize: 10,
                                fontWeight: 600,
                                padding: "2px 8px",
                                borderRadius: 6,
                                background: "#f1f5f9",
                                color: "#475569",
                                border: "1px solid #e2e8f0"
                              }}>
                                Subject: {group.subject_name}
                              </span>
                            )}
                            {group.total_marks != null && (
                              <span style={{
                                fontSize: 10,
                                fontWeight: 600,
                                padding: "2px 8px",
                                borderRadius: 6,
                                background: "#f1f5f9",
                                color: "#475569",
                                border: "1px solid #e2e8f0"
                              }}>
                                Max Marks: {group.total_marks}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Header right: Count badge & Results link */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "4px 10px",
                          borderRadius: 20,
                          background: "#dcfce7",
                          color: "#166534",
                          border: "1px solid #bbf7d0"
                        }}>
                          {group.items.length} {group.items.length === 1 ? "Certificate" : "Certificates"} Issued
                        </span>
                        {group.exam_id && (
                          <button
                            onClick={() => nav(resultsPath)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              padding: "5px 12px",
                              borderRadius: 7,
                              border: "1.5px solid #cbd5e1",
                              background: "#fff",
                              color: "#475569",
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: "pointer",
                              transition: "all 0.15s"
                            }}
                          >
                            <ExternalLink size={12} /> View Exam Results
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Student Certificates Grid for this Exam */}
                    <div style={{ padding: 18 }}>
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))",
                        gap: 14
                      }}>
                        {group.items.map((c) => {
                          const gradeStyle = getGradeStyle(c.grade);
                          return (
                            <div
                              key={c.id}
                              style={{
                                background: "#ffffff",
                                borderRadius: 12,
                                border: "1.5px solid #e2e8f0",
                                padding: 14,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                transition: "transform 0.15s, box-shadow 0.15s",
                                boxShadow: "0 1px 4px rgba(0,0,0,0.03)"
                              }}
                            >
                              <div>
                                {/* Card Header: Avatar, Name, Code, Issued status */}
                                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                                  <div style={{
                                    width: 38, height: 38, borderRadius: 10,
                                    background: "linear-gradient(135deg, #f59e0b, #d97706)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    color: "#fff", flexShrink: 0
                                  }}>
                                    <Award size={18} />
                                  </div>
                                  <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{
                                      fontWeight: 700,
                                      fontSize: 13,
                                      color: "#0f172a",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis"
                                    }}>
                                      {c.student_name || "Student"}
                                    </div>
                                    <div style={{ fontSize: 11, color: "#64748b" }}>
                                      {c.student_code || "ADM-000"}
                                    </div>
                                  </div>
                                  <span style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    padding: "2px 7px",
                                    borderRadius: 12,
                                    background: "#dcfce7",
                                    color: "#166534"
                                  }}>
                                    Issued
                                  </span>
                                </div>

                                {/* Marks and Grade row */}
                                <div style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: "8px 10px",
                                  background: "#f8fafc",
                                  borderRadius: 8,
                                  marginBottom: 10,
                                  fontSize: 11
                                }}>
                                  <div>
                                    <span style={{ color: "#64748b" }}>Marks: </span>
                                    <strong style={{ color: "#1e40af", fontSize: 12 }}>
                                      {c.marks_obtained != null ? Number(c.marks_obtained).toFixed(2) : "N/A"}
                                    </strong>
                                    {group.total_marks && (
                                      <span style={{ color: "#94a3b8", fontSize: 10 }}> / {group.total_marks}</span>
                                    )}
                                  </div>
                                  {c.grade && (
                                    <span style={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      padding: "2px 8px",
                                      borderRadius: 6,
                                      background: gradeStyle.bg,
                                      color: gradeStyle.color
                                    }}>
                                      {c.grade}
                                    </span>
                                  )}
                                </div>

                                {/* Issue Date */}
                                <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 12, display: "flex", alignItems: "center", gap: 4 }}>
                                  <Calendar size={11} />
                                  Issued: {new Date(c.issued_at).toLocaleDateString("en-IN", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric"
                                  })}
                                </div>
                              </div>

                              {/* View & Print Action */}
                              <button
                                onClick={() => nav(`${basePath}/certificates/${c.id}`)}
                                style={{
                                  width: "100%",
                                  padding: "7px 0",
                                  borderRadius: 8,
                                  border: "1.5px solid #2563eb",
                                  background: "#eff6ff",
                                  color: "#1d4ed8",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 6,
                                  transition: "all 0.15s"
                                }}
                              >
                                <Download size={13} /> View & Print Certificate
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
